import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Description as FileIcon,
  Image as ImageIcon,
  VideoFile as VideoIcon,
  AudioFile as AudioIcon,
  PictureAsPdf as PdfIcon,
  Archive as ArchiveIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import S3FileRetrievalService from "../../services/S3FileRetrievalService";
import EvidanceaccessServices from "services/EvidanceaccessServices";
import { useSelector } from "react-redux";

// Styled components with enhanced security and design
const FileCard = styled(Card)(({ theme, fileType, status }) => ({
  cursor:
    ["image", "video", "audio", "pdf"].includes(fileType) && status === "ready"
      ? "pointer"
      : "default",
  transition: "all 0.3s ease-in-out",
  border: `2px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  overflow: "hidden",
  position: "relative",
  background: "linear-gradient(135deg, #fff 0%, #fafafa 100%)",

  // Security overlay to prevent screenshots
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "transparent",
    zIndex: 1,
    pointerEvents: "none",
  },

  "&:hover": {
    transform:
      ["image", "video", "audio", "pdf"].includes(fileType) &&
      status === "ready"
        ? "translateY(-4px)"
        : "none",
    boxShadow:
      ["image", "video", "audio", "pdf"].includes(fileType) &&
      status === "ready"
        ? "0 8px 32px rgba(0,0,0,0.15)"
        : "0 4px 20px rgba(0,0,0,0.08)",
    borderColor: theme.palette.primary.main,
  },

  ...(status === "loading" && {
    borderColor: theme.palette.primary.main,
    background: `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.primary.light}25 100%)`,
    animation: "pulse 2s infinite",
  }),

  ...(status === "error" && {
    borderColor: theme.palette.error.main,
    background: `linear-gradient(135deg, ${theme.palette.error.light}15 0%, ${theme.palette.error.light}25 100%)`,
  }),

  ...(status === "ready" && {
    borderColor: theme.palette.success.light,
    background: `linear-gradient(135deg, ${theme.palette.success.light}08 0%, ${theme.palette.success.light}18 100%)`,
  }),

  "@keyframes pulse": {
    "0%": {
      opacity: 1,
    },
    "50%": {
      opacity: 0.7,
    },
    "100%": {
      opacity: 1,
    },
  },
}));

const FileIconContainer = styled(Box)(({ theme, fileType }) => ({
  width: 56,
  height: 56,
  borderRadius: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: `linear-gradient(135deg, ${theme.palette.primary.light}20 0%, ${theme.palette.primary.main}40 100%)`,
  color: theme.palette.primary.main,
  fontSize: "1.8rem",
  flexShrink: 0,
  position: "relative",
  zIndex: 2,
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",

  ...(fileType === "image" && {
    background: `linear-gradient(135deg, ${theme.palette.info.light}20 0%, ${theme.palette.info.main}40 100%)`,
    color: theme.palette.info.main,
  }),

  ...(fileType === "video" && {
    background: `linear-gradient(135deg, ${theme.palette.warning.light}20 0%, ${theme.palette.warning.main}40 100%)`,
    color: theme.palette.warning.main,
  }),

  ...(fileType === "pdf" && {
    background: `linear-gradient(135deg, ${theme.palette.error.light}20 0%, ${theme.palette.error.main}40 100%)`,
    color: theme.palette.error.main,
  }),
}));

const StatusIndicator = styled(Chip)(({ theme, status }) => ({
  height: 24,
  fontSize: "0.65rem",
  fontWeight: "700",
  borderRadius: theme.spacing(3),
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  "& .MuiChip-label": {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },

  ...(status === "ready" && {
    background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
    color: "white",
    boxShadow: "0 2px 8px rgba(76, 175, 80, 0.3)",
  }),

  ...(status === "loading" && {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: "white",
    boxShadow: "0 2px 8px rgba(25, 118, 210, 0.3)",
  }),

  ...(status === "error" && {
    background: `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`,
    color: "white",
    boxShadow: "0 2px 8px rgba(244, 67, 54, 0.3)",
  }),
}));

const SecurePreviewDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    maxWidth: "90vw",
    maxHeight: "90vh",
    margin: theme.spacing(2),
    borderRadius: theme.spacing(3),
    overflow: "hidden",
    background: "linear-gradient(135deg, #fff 0%, #f8f9fa 100%)",
    boxShadow: "0 24px 64px rgba(0,0,0,0.2)",
    border: `3px solid ${theme.palette.primary.main}`,

    // Security measures to prevent screenshots/recording
    "-webkit-user-select": "none",
    "-moz-user-select": "none",
    "-ms-user-select": "none",
    "user-select": "none",
    "-webkit-touch-callout": "none",
    "-webkit-tap-highlight-color": "transparent",
  },

  "& .MuiBackdrop-root": {
    backdropFilter: "blur(8px)",
    backgroundColor: "rgba(0,0,0,0.7)",
  },
}));

const S3FileRetrieval = ({
  evidence_items = [], // Array of evidence items with case_evidence_item_id and key
  autoLoad = true, // Whether to auto-load files on mount
  showPreview = true, // Whether to show preview functionality
  showRefresh = true, // Whether to show refresh functionality
  layout = "grid", // 'grid', 'list', or 'compact'
  columns = 3, // Number of columns in grid layout
  title = null, // Optional title for the component
  sx = {},
  onFileLoad = null, // Callback when file is loaded
  onFileError = null, // Callback when file fails to load
  onFileView = null, // Callback when file is viewed (replaces download callback)
  case_id = null, // Optional case ID for tracking
}) => {
  const user = useSelector((state) => state.auth.user);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const fetchingRef = useRef(false);
  const [isSnippingDetected, setIsSnippingDetected] = useState(false);
  const [securityOverlayActive, setSecurityOverlayActive] = useState(false);
  const overlayIntervalRef = useRef(null);
  const blurTimeoutRef = useRef(null);
  console.log("evidence_items", evidence_items);
  // Normalize evidence_items - extract keys and case_evidence_item_ids
  const normalizedItems = React.useMemo(() => {
    return Array.isArray(evidence_items) ? evidence_items : [];
  }, [evidence_items]);

  const normalizedKeys = React.useMemo(() => {
    return normalizedItems.map((item) => item.key).filter(Boolean);
  }, [normalizedItems]);

  // Load files data
  const loadFiles = useCallback(async () => {
    if (normalizedKeys.length === 0) {
      setFiles([]);
      setError("No evidence items provided");
      return;
    }

    // Prevent multiple simultaneous fetches
    if (fetchingRef.current) {
      console.log("Already fetching, skipping...");
      return;
    }

    fetchingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      let result;
      // Always use multiple file logic since we work with evidence_items array
      if (normalizedKeys.length > 1) {
        const multipleResult =
          await S3FileRetrievalService.getMultipleDownloadUrls(normalizedKeys);

        console.log("Multiple result from service:", multipleResult);

        // Transform service response to files array
        const files = [];

        if (
          multipleResult.success ||
          multipleResult.data?.successful ||
          multipleResult.data?.failed
        ) {
          // Add successful files
          if (multipleResult.data?.successful) {
            multipleResult.data.successful.forEach((item) => {
              const evidenceItem = normalizedItems.find(
                (ei) => ei.key === item.key
              );
              files.push({
                key: item.key,
                case_evidence_item_id:
                  evidenceItem?.case_evidence_item_id || null,
                downloadUrl: item.url,
                filename: S3FileRetrievalService.extractFilename(item.key),
                fileType: S3FileRetrievalService.getFileType(item.key),
                extension: S3FileRetrievalService.getFileExtension(item.key),
                status: "ready",
              });
            });
          }

          // Add failed files
          if (multipleResult.data?.failed) {
            multipleResult.data.failed.forEach((item) => {
              const evidenceItem = normalizedItems.find(
                (ei) => ei.key === item.key
              );
              files.push({
                key: item.key,
                case_evidence_item_id:
                  evidenceItem?.case_evidence_item_id || null,
                filename: S3FileRetrievalService.extractFilename(item.key),
                fileType: S3FileRetrievalService.getFileType(item.key),
                extension: S3FileRetrievalService.getFileExtension(item.key),
                status: "error",
                error: item.error || "Failed to get download URL",
              });
            });
          }
        } else {
          // If completely failed, create error files for all keys
          normalizedItems.forEach((evidenceItem) => {
            files.push({
              key: evidenceItem.key,
              case_evidence_item_id: evidenceItem.case_evidence_item_id || null,
              filename: S3FileRetrievalService.extractFilename(
                evidenceItem.key
              ),
              fileType: S3FileRetrievalService.getFileType(evidenceItem.key),
              extension: S3FileRetrievalService.getFileExtension(
                evidenceItem.key
              ),
              status: "error",
              error:
                multipleResult.error ||
                multipleResult.message ||
                "Failed to get download URL",
            });
          });
        }

        result = { files };
      } else {
        const singleResult = await S3FileRetrievalService.getDownloadUrl(
          normalizedKeys[0]
        );

        console.log("Single result from service:", singleResult);

        // Transform service response to file object
        if (singleResult.success) {
          const evidenceItem = normalizedItems[0];
          const fileObj = {
            key: normalizedKeys[0],
            case_evidence_item_id: evidenceItem?.case_evidence_item_id || null,
            downloadUrl: singleResult.data.url,
            filename: S3FileRetrievalService.extractFilename(normalizedKeys[0]),
            fileType: S3FileRetrievalService.getFileType(normalizedKeys[0]),
            extension: S3FileRetrievalService.getFileExtension(
              normalizedKeys[0]
            ),
            status: "ready",
          };
          result = { files: [fileObj] };
        } else {
          const evidenceItem = normalizedItems[0];
          const fileObj = {
            key: normalizedKeys[0],
            case_evidence_item_id: evidenceItem?.case_evidence_item_id || null,
            filename: S3FileRetrievalService.extractFilename(normalizedKeys[0]),
            fileType: S3FileRetrievalService.getFileType(normalizedKeys[0]),
            extension: S3FileRetrievalService.getFileExtension(
              normalizedKeys[0]
            ),
            status: "error",
            error:
              singleResult.error ||
              singleResult.message ||
              "Failed to get download URL",
          };
          result = { files: [fileObj] };
        }
      }

      console.log("Processed result:", result);

      // Files are already processed with correct status and properties
      const processedFiles = result.files;

      setFiles(processedFiles);
      setHasFetched(true);

      // Call onFileLoad callback for each loaded file
      if (onFileLoad) {
        processedFiles.forEach((file) => {
          if (file.status === "ready") {
            onFileLoad(file);
          }
        });
      }

      // Call onFileError callback for each failed file
      if (onFileError) {
        processedFiles.forEach((file) => {
          if (file.status === "error") {
            onFileError(file);
          }
        });
      }
    } catch (err) {
      console.error("Error loading files:", err);
      const errorMessage = err.message || "Failed to load files";
      setError(errorMessage);

      // Create error files for display
      const errorFiles = normalizedItems.map((evidenceItem) => ({
        key: evidenceItem.key,
        case_evidence_item_id: evidenceItem.case_evidence_item_id || null,
        filename: S3FileRetrievalService.extractFilename(evidenceItem.key),
        fileType: S3FileRetrievalService.getFileType(evidenceItem.key),
        extension: S3FileRetrievalService.getFileExtension(evidenceItem.key),
        status: "error",
        error: errorMessage,
      }));
      setFiles(errorFiles);

      if (onFileError) {
        errorFiles.forEach((file) => onFileError(file));
      }
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [normalizedKeys, normalizedItems, onFileLoad, onFileError]);

  // Anti-snipping tool protection effects
  useEffect(() => {
    if (previewOpen) {
      // Detect potential snipping tool usage
      const handleKeyDown = (e) => {
        // Common snipping tool shortcuts
        const snippingShortcuts = [
          // Windows Snipping Tool shortcuts
          e.key === "S" && (e.ctrlKey || e.metaKey) && e.shiftKey,
          // PrintScreen
          e.key === "PrintScreen",
          // Alt + PrintScreen
          e.key === "PrintScreen" && e.altKey,
          // Windows + Shift + S (Snip & Sketch)
          e.key === "S" && e.metaKey && e.shiftKey,
          // Mac screenshots
          e.key === "3" && e.metaKey && e.shiftKey,
          e.key === "4" && e.metaKey && e.shiftKey,
          e.key === "5" && e.metaKey && e.shiftKey,
        ];

        if (snippingShortcuts.some(Boolean)) {
          e.preventDefault();
          e.stopPropagation();
          setIsSnippingDetected(true);
          setSecurityOverlayActive(true);

          // Auto-hide after 3 seconds
          if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
          blurTimeoutRef.current = setTimeout(() => {
            setIsSnippingDetected(false);
            setSecurityOverlayActive(false);
          }, 3000);

          return false;
        }
      };

      // Detect window blur (user might be using external tools)
      const handleWindowBlur = () => {
        setSecurityOverlayActive(true);
        setTimeout(() => setSecurityOverlayActive(false), 1000);
      };

      // Detect window focus loss and regain
      const handleVisibilityChange = () => {
        if (document.hidden) {
          setSecurityOverlayActive(true);
        } else {
          setTimeout(() => setSecurityOverlayActive(false), 500);
        }
      };

      // // Start random security overlay intervals
      // overlayIntervalRef.current = setInterval(() => {
      //   setSecurityOverlayActive(true);
      //   setTimeout(() => setSecurityOverlayActive(false), 200);
      // }, Math.random() * 5000 + 3000); // Random intervals between 3-8 seconds

      document.addEventListener("keydown", handleKeyDown, true);
      window.addEventListener("blur", handleWindowBlur);
      document.addEventListener("visibilitychange", handleVisibilityChange);

      return () => {
        document.removeEventListener("keydown", handleKeyDown, true);
        window.removeEventListener("blur", handleWindowBlur);
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
        if (overlayIntervalRef.current)
          clearInterval(overlayIntervalRef.current);
        if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
      };
    }
  }, [previewOpen]);

  // Auto-load effect
  useEffect(() => {
    if (autoLoad && normalizedKeys.length > 0 && !hasFetched) {
      loadFiles();
    }
  }, [autoLoad, normalizedKeys.length, hasFetched, loadFiles]);

  // Function to track evidence access
  const caseItemAccessInfo = useCallback(
    async (file) => {
      if (file?.case_evidence_item_id && case_id) {
        await EvidanceaccessServices.create({
          case_evidence_item_id: file.case_evidence_item_id,
          case_id: case_id,
          access_type: "VIEW",
          accessed_by: user ? user.id : null,
        });
      }
    },
    [case_id, user]
  );

  // Handle file preview
  const handlePreview = useCallback(
    (file) => {
      console.log("File Preview", file);

      setPreviewFile(file);
      setPreviewOpen(true);
      caseItemAccessInfo(file);
      // Call onFileView callback for tracking
      if (onFileView) {
        onFileView(file);
      }
    },
    [onFileView, caseItemAccessInfo]
  );

  // Get file icon based on file type
  const getFileIcon = (fileType) => {
    switch (fileType) {
      case "image":
        return <ImageIcon />;
      case "video":
        return <VideoIcon />;
      case "audio":
        return <AudioIcon />;
      case "pdf":
        return <PdfIcon />;
      case "archive":
        return <ArchiveIcon />;
      default:
        return <FileIcon />;
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case "loading":
        return <CircularProgress size={12} color="inherit" />;
      case "ready":
        return "✓";
      case "error":
        return "✗";
      default:
        return "•";
    }
  };

  // Component for rendering file icon or image
  const FileIconOrImage = ({ file }) => {
    const [imageError, setImageError] = useState(false);

    // Add some debugging
    console.log("FileIconOrImage - File:", {
      filename: file.filename,
      fileType: file.fileType,
      status: file.status,
      hasDownloadUrl: !!file.downloadUrl,
      downloadUrl: file.downloadUrl?.substring(0, 50) + "...", // Just first 50 chars for debugging
    });

    if (file.fileType === "image" && file.downloadUrl && !imageError) {
      return (
        <Box
          component="img"
          src={file.downloadUrl}
          alt={file.filename}
          draggable={false}
          onContextMenu={(e) => e.preventDefault()}
          onError={(e) => {
            console.log("Image failed to load:", {
              filename: file.filename,
              status: file.status,
              downloadUrl: file.downloadUrl,
            });
            setImageError(true);
          }}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: 1,
            display: "block",
            "-webkit-user-select": "none",
            "-moz-user-select": "none",
            "-ms-user-select": "none",
            "user-select": "none",
            "-webkit-user-drag": "none",
          }}
        />
      );
    }

    return getFileIcon(file.fileType);
  };

  // Render file card with enhanced security and styling
  const renderFileCard = (file) => {
    const canPreview =
      showPreview && ["image", "video", "audio", "pdf"].includes(file.fileType);
    const needsLoad = file.status === "waiting";

    return (
      <FileCard
        key={file.key}
        fileType={file.fileType}
        status={file.status}
        onClick={canPreview ? () => handlePreview(file) : undefined}
      >
        <CardContent sx={{ p: 3, position: "relative" }}>
          <Box display="flex" alignItems="flex-start" gap={3}>
            <FileIconContainer fileType={file.fileType}>
              <FileIconOrImage file={file} />
            </FileIconContainer>

            <Box flex={1} minWidth={0}>
              {/* Enhanced title row */}
              <Box display="flex" alignItems="center" mb={2}>
                <Typography
                  variant="h6"
                  fontWeight="700"
                  color="text.primary"
                  sx={{
                    fontSize: "1.1rem",
                    lineHeight: 1.2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    letterSpacing: "-0.01em",
                  }}
                  title={file.filename}
                >
                  {file.filename}
                </Typography>
              </Box>

              {/* Enhanced file info and tags */}
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={2}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1.5}
                  flexWrap="wrap"
                >
                  <StatusIndicator
                    status={file.status}
                    icon={getStatusIcon(file.status)}
                    label={
                      file.status?.charAt(0).toUpperCase() +
                        file.status?.slice(1) || "Unknown"
                    }
                    size="small"
                  />

                  <Chip
                    label={
                      file.fileType?.charAt(0).toUpperCase() +
                        file.fileType?.slice(1) || "Unknown"
                    }
                    size="small"
                    sx={{
                      height: 24,
                      fontSize: "0.7rem",
                      fontWeight: "700",
                      background:
                        "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
                      color: "primary.dark",
                      borderRadius: 3,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      "& .MuiChip-label": {
                        px: 1.5,
                      },
                    }}
                  />

                  {file.extension && (
                    <Chip
                      label={file.extension.toUpperCase()}
                      size="small"
                      sx={{
                        height: 24,
                        fontSize: "0.7rem",
                        fontWeight: "700",
                        background:
                          "linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)",
                        color: "secondary.dark",
                        borderRadius: 3,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        "& .MuiChip-label": {
                          px: 1.5,
                        },
                      }}
                    />
                  )}

                  {file.size && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.75rem",
                        background: "rgba(0,0,0,0.04)",
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                      }}
                    >
                      {S3FileRetrievalService.formatFileSize(file.size)}
                    </Typography>
                  )}
                </Box>

                {/* Enhanced action buttons */}
                <Box display="flex" gap={1.5}>
                  {/* Load File Button */}
                  {needsLoad && (
                    <Button
                      size="medium"
                      variant="contained"
                      onClick={(e) => {
                        e.stopPropagation();
                        loadFiles();
                      }}
                      startIcon={<RefreshIcon fontSize="small" />}
                      sx={{
                        fontSize: "0.75rem",
                        textTransform: "none",
                        fontWeight: "700",
                        height: "36px",
                        minWidth: "100px",
                        borderRadius: 2,
                        background:
                          "linear-gradient(135deg, #2196f3 0%, #1976d2 100%)",
                        boxShadow: "0 4px 12px rgba(33, 150, 243, 0.4)",
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                          transform: "translateY(-1px)",
                          boxShadow: "0 6px 16px rgba(33, 150, 243, 0.5)",
                        },
                      }}
                    >
                      Load File
                    </Button>
                  )}

                  {/* Secure View Button for loaded files */}
                  {file.status === "ready" && canPreview && (
                    <Button
                      size="medium"
                      variant="contained"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePreview(file);
                      }}
                      startIcon={<VisibilityIcon fontSize="small" />}
                      sx={{
                        fontSize: "0.75rem",
                        textTransform: "none",
                        fontWeight: "700",
                        height: "36px",
                        minWidth: "120px",
                        borderRadius: 2,
                        background:
                          "linear-gradient(135deg, #4caf50 0%, #388e3c 100%)",
                        boxShadow: "0 4px 12px rgba(76, 175, 80, 0.4)",
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)",
                          transform: "translateY(-1px)",
                          boxShadow: "0 6px 16px rgba(76, 175, 80, 0.5)",
                        },
                      }}
                    >
                      View File
                    </Button>
                  )}
                </Box>
              </Box>

              {/* Enhanced error display */}
              {file.status === "error" && (
                <Alert
                  severity="error"
                  sx={{
                    mt: 2,
                    py: 1,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "error.light",
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight="600"
                    sx={{ fontSize: "0.8rem" }}
                  >
                    {file.error}
                  </Typography>
                </Alert>
              )}
            </Box>
          </Box>
        </CardContent>
      </FileCard>
    );
  };

  // Render files based on layout
  const renderFiles = () => {
    if (layout === "grid") {
      return (
        <Grid container spacing={2}>
          {files.map((file) => (
            <Grid size={{ xs: 12, md: 12 / columns, sm: 6 }} key={file.key}>
              {renderFileCard(file)}
            </Grid>
          ))}
        </Grid>
      );
    } else {
      return (
        <Box display="flex" flexDirection="column" gap={2}>
          {files.map((file) => renderFileCard(file))}
        </Box>
      );
    }
  };

  // Enhanced secure preview component with anti-snipping measures
  const renderSecurityOverlay = () => {
    if (!securityOverlayActive && !isSnippingDetected) return null;

    return (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: isSnippingDetected
            ? "repeating-linear-gradient(45deg, #ff0000 0px, #ff0000 20px, #000000 20px, #000000 40px)"
            : "rgba(255,255,255,0.9)",
          zIndex: 99999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backdropFilter: "blur(20px)",
          pointerEvents: "none",
          animation: isSnippingDetected
            ? "securityFlash 0.1s infinite alternate"
            : "securityPulse 2s infinite",
          "@keyframes securityFlash": {
            "0%": { opacity: 1, filter: "hue-rotate(0deg)" },
            "100%": { opacity: 0.3, filter: "hue-rotate(180deg)" },
          },
          "@keyframes securityPulse": {
            "0%": { opacity: 0.1 },
            "50%": { opacity: 0.3 },
            "100%": { opacity: 0.1 },
          },
        }}
      >
        {isSnippingDetected && (
          <Typography
            variant="h1"
            sx={{
              color: "#ff0000",
              fontWeight: 900,
              textShadow: "4px 4px 8px rgba(0,0,0,0.8)",
              transform: "rotate(-15deg)",
              fontSize: "8rem",
              userSelect: "none",
              animation: "bounce 0.5s infinite",
              "@keyframes bounce": {
                "0%, 100%": { transform: "rotate(-15deg) scale(1)" },
                "50%": { transform: "rotate(15deg) scale(1.1)" },
              },
            }}
          >
            🚫 CAPTURE BLOCKED 🚫
          </Typography>
        )}
      </Box>
    );
  };

  // Enhanced secure preview component with anti-snipping measures
  const renderPreview = () => {
    if (!previewFile) return null;

    const { fileType, downloadUrl, filename } = previewFile;

    return (
      <SecurePreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="xl"
        fullWidth
        disableEscapeKeyDown={true} // Prevent ESC key for confidential files
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 2,
            pt: 3,
            px: 4,
            background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
            color: "white",
            borderBottom: "2px solid",
            borderColor: "primary.dark",
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Typography
              variant="h5"
              noWrap
              sx={{
                fontWeight: 700,
                fontSize: "1.3rem",
                letterSpacing: "-0.02em",
              }}
            >
              {filename}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setPreviewOpen(false)}
            size="medium"
            sx={{
              ml: 2,
              backgroundColor: "rgba(255,255,255,0.2)",
              color: "white",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.3)",
                transform: "scale(1.05)",
              },
            }}
          >
            <CloseIcon fontSize="medium" />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 0,
            backgroundColor: "#f8f9fa",
            height: "80vh", // Fixed height to prevent scrollbars
            display: "flex",
            flexDirection: "column",
            position: "relative",
            overflow: "hidden", // Prevent any overflow scrollbars
            // Security measures to prevent content capture
            "-webkit-user-select": "none",
            "-moz-user-select": "none",
            "-ms-user-select": "none",
            "user-select": "none",
            "-webkit-touch-callout": "none",
            "-webkit-tap-highlight-color": "transparent",
            // Advanced anti-snipping measures
            filter: securityOverlayActive ? "blur(20px) contrast(0.1)" : "none",
            transition: "filter 0.1s ease-in-out",
            // Prevent content indexing
            "*": {
              "-webkit-user-select": "none",
              "-moz-user-select": "none",
              "user-select": "none",
              "pointer-events": securityOverlayActive ? "none" : "auto",
            },
          }}
        >
          {/* Dynamic security overlay */}
          {(securityOverlayActive || isSnippingDetected) && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: isSnippingDetected
                  ? "repeating-linear-gradient(45deg, rgba(255,0,0,0.8) 0px, rgba(255,0,0,0.8) 20px, rgba(0,0,0,0.9) 20px, rgba(0,0,0,0.9) 40px)"
                  : "linear-gradient(45deg, rgba(255,255,255,0.9) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.9) 50%, rgba(255,255,255,0.9) 75%, transparent 75%, transparent)",
                backgroundSize: "40px 40px",
                zIndex: 1000,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                animation: isSnippingDetected
                  ? "emergencyFlash 0.1s infinite"
                  : "subtleMove 3s infinite linear",
                "@keyframes emergencyFlash": {
                  "0%": { opacity: 1, filter: "hue-rotate(0deg)" },
                  "100%": { opacity: 0.3, filter: "hue-rotate(180deg)" },
                },
                "@keyframes subtleMove": {
                  "0%": { backgroundPosition: "0 0" },
                  "100%": { backgroundPosition: "40px 40px" },
                },
              }}
            >
              {isSnippingDetected && (
                <Typography
                  variant="h3"
                  sx={{
                    color: "#ff0000",
                    fontWeight: 900,
                    textShadow: "2px 2px 4px rgba(0,0,0,0.8)",
                    transform: "rotate(-15deg)",
                    userSelect: "none",
                    fontSize: "3rem",
                  }}
                >
                  🚫 SCREENSHOT BLOCKED 🚫
                </Typography>
              )}
            </Box>
          )}
          {/* Enhanced Image Preview */}
          {fileType === "image" && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "#e9e9e9ff",
                height: "100%", // Use full dialog content height
                position: "relative",
                overflow: "hidden",
                p: 2, // Small padding for visual spacing
              }}
            >
              <Box
                component="img"
                src={downloadUrl}
                alt={filename}
                draggable={false} // Prevent dragging
                onContextMenu={(e) => e.preventDefault()} // Disable right-click
                sx={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  width: "auto",
                  height: "auto",
                  display: "block",
                  objectFit: "contain",
                  borderRadius: 2,
                  boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
                  // Security measures
                  pointerEvents: "none",
                  "-webkit-user-drag": "none",
                  "-webkit-user-select": "none",
                  // Add subtle noise to make screenshot recognition harder
                  filter: "contrast(1.02) brightness(1.01)",
                }}
              />

              {/* Dynamic anti-capture noise overlay */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    "radial-gradient(circle at 50% 50%, transparent 40%, rgba(255,255,255,0.01) 60%)",
                  pointerEvents: "none",
                  mixBlendMode: "overlay",
                  animation: "antiCaptureNoise 5s infinite linear",
                  "@keyframes antiCaptureNoise": {
                    "0%": {
                      backgroundPosition: "0 0",
                      filter: "hue-rotate(0deg)",
                    },
                    "25%": {
                      backgroundPosition: "100px 50px",
                      filter: "hue-rotate(90deg)",
                    },
                    "50%": {
                      backgroundPosition: "-50px 100px",
                      filter: "hue-rotate(180deg)",
                    },
                    "75%": {
                      backgroundPosition: "75px -25px",
                      filter: "hue-rotate(270deg)",
                    },
                    "100%": {
                      backgroundPosition: "0 0",
                      filter: "hue-rotate(360deg)",
                    },
                  },
                }}
              />
            </Box>
          )}

          {/* Enhanced Video Preview */}
          {fileType === "video" && (
            <Box
              sx={{
                backgroundColor: "#e9e9e9ff",
                height: "100%", // Use full dialog content height
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                position: "relative",
                overflow: "hidden", // Prevent scrollbars
                p: 2, // Small padding for visual spacing
              }}
            >
              <Box
                component="video"
                controls
                controlsList={"nodownload"}
                disablePictureInPicture={true}
                onContextMenu={(e) => e.preventDefault()}
                src={downloadUrl}
                sx={{
                  width: "100%", // Full width of container
                  height: "100%", // Full height of container
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain", // Maintain aspect ratio without overflow
                  display: "block",
                  borderRadius: 2,
                  boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
                  "&:focus": {
                    outline: "2px solid",
                    outlineColor: "primary.main",
                  },
                }}
              />
            </Box>
          )}

          {/* Enhanced Audio Preview */}
          {fileType === "audio" && (
            <Box
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                height: "100%", // Use full dialog content height
                p: 4,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 4,
                color: "white",
                overflow: "hidden", // Prevent scrollbars
              }}
            >
              <Box
                sx={{
                  p: 4,
                  borderRadius: "50%",
                  backgroundColor: "rgba(255,255,255,0.2)",
                  backdropFilter: "blur(10px)",
                }}
              >
                <AudioIcon sx={{ fontSize: "6rem", color: "white" }} />
              </Box>

              <Typography variant="h4" fontWeight="700" textAlign="center">
                {filename}
              </Typography>

              <Box
                component="audio"
                controls
                controlsList={"nodownload"}
                onContextMenu={(e) => e.preventDefault()}
                src={downloadUrl}
                sx={{
                  width: "100%",
                  maxWidth: "600px",
                  height: "60px",
                  borderRadius: 2,
                  backgroundColor: "rgba(255,255,255,0.1)",
                  backdropFilter: "blur(10px)",
                }}
              />
            </Box>
          )}

          {/* Enhanced PDF Preview */}
          {fileType === "pdf" && (
            <Box
              sx={{
                backgroundColor: "#fff",
                height: "100%", // Use full dialog content height
                display: "flex",
                flexDirection: "column",
                position: "relative",
                overflow: "hidden", // Prevent scrollbars
              }}
            >
              <Box
                component="iframe"
                src={`${downloadUrl}#toolbar=${1}&navpanes=0&scrollbar=1`}
                onContextMenu={(e) => e.preventDefault()}
                sx={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                  display: "block",
                  backgroundColor: "#fff",
                  flex: 1, // Take remaining space
                }}
              />
            </Box>
          )}
        </DialogContent>
      </SecurePreviewDialog>
    );
  };

  // Loading state
  if (loading && files.length === 0) {
    return (
      <Box sx={{ ...sx, textAlign: "center", py: 4 }}>
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading files...
        </Typography>
      </Box>
    );
  }

  // Error state (when no files loaded)
  if (error && files.length === 0) {
    return (
      <Box sx={{ ...sx }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        {showRefresh && (
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadFiles}
            sx={{ mt: 1 }}
          >
            Retry
          </Button>
        )}
      </Box>
    );
  }

  // Empty state
  if (files.length === 0) {
    return (
      <Box sx={{ ...sx, textAlign: "center", py: 6 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
            p: 4,
            borderRadius: 3,
            background: "linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)",
            border: "2px dashed #ccc",
          }}
        >
          <FileIcon sx={{ fontSize: "4rem", color: "text.secondary" }} />
          <Typography variant="h6" color="text.secondary" fontWeight="600">
            No files available to view
          </Typography>
          {!autoLoad && showRefresh && (
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={loadFiles}
              sx={{
                mt: 2,
                borderRadius: 2,
                px: 4,
                py: 1.5,
                background: "linear-gradient(135deg, #2196f3 0%, #1976d2 100%)",
                fontWeight: "700",
                textTransform: "none",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                  transform: "translateY(-1px)",
                },
              }}
            >
              Load Files
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...sx,
        // Global security measures for the entire componen
        "-webkit-user-select": "none",
        "-moz-user-select": "none",
        "-ms-user-select": "none",
        "user-select": "none",
        "-webkit-touch-callout": "none",
        "-webkit-tap-highlight-color": "transparent",
      }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* Enhanced Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          p: 3,
          borderRadius: 3,
          background: "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
          border: "1px solid",
          borderColor: "primary.light",
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          {title && (
            <Typography
              variant="h5"
              component="h2"
              sx={{
                fontWeight: 700,
                color: "primary.dark",
                letterSpacing: "-0.02em",
              }}
            >
              {title}
            </Typography>
          )}

          <Chip
            label={`${files.length} ${files.length === 1 ? "File" : "Files"}`}
            color="primary"
            variant="outlined"
            sx={{
              fontWeight: "600",
              fontSize: "0.75rem",
            }}
          />
        </Box>

        {showRefresh && (
          <Button
            variant="outlined"
            size="medium"
            startIcon={<RefreshIcon />}
            onClick={loadFiles}
            disabled={loading}
            sx={{
              borderRadius: 2,
              fontWeight: "600",
              textTransform: "none",
              px: 3,
              borderColor: "primary.main",
              color: "primary.main",
              "&:hover": {
                backgroundColor: "primary.main",
                color: "white",
                transform: "translateY(-1px)",
              },
            }}
          >
            Refresh Files
          </Button>
        )}
      </Box>

      {/* Files Grid/List */}
      {renderFiles()}

      {/* Enhanced Security Preview Dialog */}
      {renderPreview()}

      {/* Global Security Overlay for Snipping Tool Detection */}
      {renderSecurityOverlay()}
    </Box>
  );
};

export default S3FileRetrieval;

// Enhanced Secure Evidence File Retrieval Example (Confidential Files)
//  <Grid size={{ xs: 12 }}>
//         <S3FileRetrieval
//           evidence_items={[
//             {
//               case_evidence_item_id:
//                 "bdfd52d8-cede-484e-a894-1b78fc05a8c5",
//               key: "evidence/temp-ab8415b5-dafe-4ede-86fb-73f7f3a43636/69b7c470-4ce3-4a63-9755-38a82e7d6001/1764688008377_normal_user.mp4",
//             },
//             {
//               case_evidence_item_id:
//                 "33cc8a46-4e93-4e39-8d60-13e4736b17b5",
//               key: "evidence/temp-576fda00-30b5-419f-80f0-d68a4c492a18/other_evidence/1764697224654_test.jpg",
//             },
//           ]}
//
//           autoLoad={true}
//           showPreview={true}
//           showRefresh={true}
//           title="Evidence Files"
//           layout="grid"
//           columns={2}
//           onFileView={(file) =>
//             console.log("File viewed securely:", file)
//           }
//           case_id={"ad12658f-c8e7-4c20-aa72-9be15a941003"}
//         />
//       </Grid>

// Security Features Added:
// - Anti-screenshot overlay and watermarks for confidential images
// - Disabled right-click context menu on confidential files
// - Disabled text selection on confidential content
// - Removed download functionality entirely for security
// - Enhanced PDF viewer with toolbar controls for confidential files
// - Video/audio controls restricted for confidential media
// - Secure preview dialogs with security banners
// - Screen recording prevention measures implemented
